/**
 * THE SHARE PAGE — `/agent/a/:token`. A URL you can paste into a channel
 * without thinking about it.
 *
 * ══ WHAT A SHARE LINK IS ═══════════════════════════════════════════════════
 *
 * A RE-RUNNABLE POINTER to an invocation, opened by an admin, resolved under
 * THAT admin's permissions. Never a frozen number, never a public URL, and
 * never a way to hand somebody data their role withholds.
 *
 * ══ 🔴 THERE IS NO `RequirePermission` ON THIS ROUTE, AND THAT IS DELIBERATE ═
 *
 * The browser holds an OPAQUE TOKEN and nothing else. There is no client-side
 * catalogue in `frontend/admin/src`, so the page cannot know which page keys
 * the underlying metric needs; any wrapper here would be a GUESS, and a guessed
 * key on the surface an operator looks at is weaker than the backend
 * conjunction, in the permissive direction. `RequirePermission`'s array form is
 * ANY-of, which would gate a two-key metric on EITHER key — weaker still. The
 * backend re-runs the metric under the CALLER's `AdminUser` and hands back a
 * `PermissionRedactedCell` naming the missing key, with NO numeric field of any
 * kind. `tests/unit/lint/agent-chat-no-remote-render.test.ts` pins the absence
 * of a wrapper on this route so the deletion is permanent.
 *
 * The route still sits under `/v1/admin/*` server-side, so `verifyAdminAuth`
 * runs before the handler exists as a possibility: a share is ADMIN-TO-ADMIN.
 * There is no anonymous share, no e-mail share and no expiry-less public page.
 *
 * ══ DESIGNED FOR THE RECIPIENT WHO SEES LESS ═══════════════════════════════
 *
 * The redacted rendering is the FIRST-CLASS case here, not an error path. It
 * says plainly that the number was never copied into the link, so a recipient
 * reads "your role does not include this" rather than "this link is broken".
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RefreshCw, Share2 } from 'lucide-react';

import { useSharedAnswer } from '@/hooks/useAgentArtifacts';

import { AnswerFigures } from './components/AnswerFigures';

export function AgentSharePage() {
  const { token } = useParams<{ token: string }>();
  const { answer, loading, notFound, error, reload } = useSharedAnswer(token);

  // THEN vs NOW. The first watermark this browser saw is kept so a re-run can
  // show the movement — which is what makes "never a frozen number" VISIBLE
  // rather than merely true.
  const [firstSeenAsOf, setFirstSeenAsOf] = useState<string | null>(null);
  useEffect(() => {
    if (answer?.as_of && firstSeenAsOf === null) setFirstSeenAsOf(answer.as_of);
  }, [answer, firstSeenAsOf]);

  if (loading) {
    return (
      <div className="space-y-3" aria-busy="true">
        <div className="h-6 w-1/3 motion-safe:animate-pulse rounded bg-muted" />
        <div className="h-40 w-full motion-safe:animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="rounded-lg border bg-card p-8 text-center">
        <h2 className="text-xl font-semibold">This link does not resolve</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Either the token is unknown or the pin behind it was removed — unpinning is the only
          revocation, so a deleted pin kills every link to it. The API answers both cases
          identically on purpose, and this page will not guess which one happened.
        </p>
      </div>
    );
  }

  if (error !== null || answer === null) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4" role="alert">
        <h2 className="text-sm font-semibold">Could not open the shared answer</h2>
        <p className="mt-1 text-sm text-muted-foreground">{error ?? 'No answer was returned.'}</p>
        <button
          type="button"
          onClick={reload}
          className="mt-3 inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs transition-colors hover:bg-muted"
        >
          <RefreshCw className="h-3 w-3" aria-hidden />
          Try again
        </button>
      </div>
    );
  }


  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-2xl font-bold">
            <Share2 className="h-5 w-5 text-muted-foreground" aria-hidden />
            {answer.title}
          </h2>
          {answer.label && (
            <p className="text-sm text-muted-foreground">Saved as “{answer.label}”</p>
          )}
        </div>
        <button
          type="button"
          onClick={reload}
          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs transition-colors hover:bg-muted"
        >
          <RefreshCw className="h-3 w-3" aria-hidden />
          Re-run
        </button>
      </header>

      <p className="rounded-md border bg-muted/40 p-2 text-xs text-muted-foreground" role="note">
        This link carries no numbers. Opening it re-runs the saved invocation under{' '}
        <strong>your</strong> permissions, so what you see may be less than the person who
        shared it sees — and it is always today’s data, never a snapshot.
      </p>

      <AnswerFigures answer={answer} firstSeenAsOf={firstSeenAsOf} />
    </div>
  );
}

export default AgentSharePage;
